import React from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';

import {
  BG_1,
  BORDER_2,
  FG_1,
  FG_3,
  INFO_BG,
  INFO_COLOR,
  INFO_TEXT,
  PRIMARY_COLOR,
  PRIMARY_LIGHT_RGBA_14,
  RADIUS_LG,
  SHADOW_SM,
  SUCCESS_BG,
  SUCCESS_COLOR,
  SUCCESS_TEXT,
  WARNING_BG,
  WARNING_COLOR,
  WARNING_TEXT,
} from 'components/upb_viewer_beta/css/betaTokens';

const clampPct = n => Math.max(0, Math.min(100, n || 0));

// Accent per card variant: colored top stripe, pill and progress bar.
const ACCENTS = {
  primary: { color: PRIMARY_COLOR, pillBg: PRIMARY_LIGHT_RGBA_14, pillText: PRIMARY_COLOR },
  success: { color: SUCCESS_COLOR, pillBg: SUCCESS_BG, pillText: SUCCESS_TEXT },
  info: { color: INFO_COLOR, pillBg: INFO_BG, pillText: INFO_TEXT },
  warning: { color: WARNING_COLOR, pillBg: WARNING_BG, pillText: WARNING_TEXT },
};

/**
 * InfluenceStatCard — a band-tile-style headline card: colored top stripe, accent pill,
 * big value, caption, an optional progress bar (ratio 0-100) and a footer line. Used by the
 * overview cohort summary and the distributor drill-in GMV card.
 */
function InfluenceStatCard({ label, value, caption, footer, ratio, accent }) {
  const a = ACCENTS[accent] || ACCENTS.primary;
  return (
    <div className={css(styles.tile)}>
      <span className={css(styles.stripe)} style={{ backgroundColor: a.color }} />
      <span className={css(styles.pill)} style={{ backgroundColor: a.pillBg, color: a.pillText }}>
        {label}
      </span>
      <div className={css(styles.value)}>{value}</div>
      {caption ? <div className={css(styles.caption)}>{caption}</div> : null}
      {ratio != null ? (
        <div className={css(styles.track)}>
          <span
            className={css(styles.fill)}
            style={{ width: `${clampPct(ratio)}%`, backgroundColor: a.color }}
          />
        </div>
      ) : null}
      {footer ? <div className={css(styles.footer)}>{footer}</div> : null}
    </div>
  );
}

const styles = StyleSheet.create({
  tile: {
    position: 'relative',
    flex: '1 1 240px',
    background: BG_1,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_SM,
    padding: '18px 20px 16px',
    overflow: 'hidden',
  },
  stripe: { position: 'absolute', top: 0, left: 0, right: 0, height: 3 },
  pill: {
    display: 'inline-block',
    fontSize: 11.5,
    fontWeight: 700,
    letterSpacing: 0.3,
    padding: '3px 10px',
    borderRadius: 20,
    marginBottom: 12,
  },
  value: {
    fontSize: 30,
    fontWeight: 700,
    color: FG_1,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.1,
  },
  caption: { fontSize: 12.5, color: FG_3, margin: '6px 0 12px' },
  track: { height: 6, borderRadius: 6, backgroundColor: BORDER_2, overflow: 'hidden' },
  fill: { display: 'block', height: '100%', borderRadius: 6 },
  footer: { fontSize: 12.5, color: FG_3, marginTop: 10, fontVariantNumeric: 'tabular-nums' },
});

InfluenceStatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  caption: PropTypes.string,
  footer: PropTypes.node,
  ratio: PropTypes.number,
  accent: PropTypes.oneOf(['primary', 'success', 'info', 'warning']),
};

InfluenceStatCard.defaultProps = {
  caption: null,
  footer: null,
  ratio: null,
  accent: 'primary',
};

export default InfluenceStatCard;
